import { type IFile } from "@services/jointBillServices/types";
import {
  type IBillCertificate,
  type IBillData,
  type IBillDetail,
  type IDonationData,
  type IJointBillServicePeriodData,
  type IOverviewJointBillService,
} from "@services/overview/types";
import { api } from "@/api";
import type {
  IBillDetailMeterRulesResponse,
  IUpdateBillDetailSettingsRequestParams,
  IUpdateBillSelectionRequestParams,
  IUpdateBillTotalExpectedPaymentAmountRequestParams,
  IUpdateHouseBillsSelectionRequestData,
} from "./types";

export const updateBillSelection = ({
  billId,
  isSelected,
}: IUpdateBillSelectionRequestParams): Promise<string> =>
  api
    .put<string>(`/bills/${billId}/selection`, null, { params: { isSelected } })
    .then((r) => r.data);

export const updateHouseBillsSelection = (
  data: IUpdateHouseBillsSelectionRequestData,
): Promise<string> =>
  api.put(
    `/bills/selection`,
    { isSelected: data.isSelected, periodId: data.periodId },
    { headers: { jointBillServiceId: data.jointBillServiceId } },
  );

export const updateBillTotalExpectedPaymentAmount = ({
  billId,
  totalExpectedPaymentAmount,
}: IUpdateBillTotalExpectedPaymentAmountRequestParams): Promise<IJointBillServicePeriodData> =>
  api
    .put<IJointBillServicePeriodData>(`/bills/${billId}/expected-payment`, {
      expectedPayment: totalExpectedPaymentAmount,
    })
    .then((r) => r.data);

export const updateBillDetailValueFrom = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
  periodId: number,
  billDetailId: IBillDetail["billDetailId"],
  valueFrom: number,
): Promise<IBillData> =>
  api
    .put<IBillData>(
      `/bill-details/${billDetailId}/value-from`,
      {
        periodId,
        valueFrom,
      },
      { headers: { jointBillServiceId: jointBillServiceId } },
    )
    .then((r) => r.data);

export const updateBillDetailValueTo = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
  periodId: number,
  billDetailId: IBillDetail["billDetailId"],
  valueTo: number,
): Promise<IBillData> =>
  api
    .put<IBillData>(
      `/bill-details/${billDetailId}/value-to`,
      {
        periodId,
        valueTo,
      },
      { headers: { jointBillServiceId: jointBillServiceId } },
    )
    .then((r) => r.data);

export const updateBillDetailTariff = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
  periodId: number,
  billDetailId: IBillDetail["billDetailId"],
  tariff: number,
): Promise<IBillData> =>
  api
    .put<IBillData>(
      `/bill-details/${billDetailId}/tariffs`,
      {
        periodId,
        tariff,
      },
      { headers: { jointBillServiceId: jointBillServiceId } },
    )
    .then((r) => r.data);

export const updateBillDetailExpectedPaymentAmount = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
  billDetailId: IBillDetail["billDetailId"],
  periodId: number,
  expectedPayment: number,
): Promise<IBillData> =>
  api
    .put<IBillData>(
      `/bill-details/${billDetailId}/expected-payments`,
      {
        periodId,
        expectedPayment,
      },
      { headers: { jointBillServiceId: jointBillServiceId } },
    )
    .then((r) => r.data);

export const updateBillDetailSettings = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
  jointBillServiceAgreementId: IBillData["jointBillServiceAgreementId"],
  billDetailId: IBillDetail["billDetailId"],
  data: IUpdateBillDetailSettingsRequestParams,
): Promise<string> =>
  api
    .put<string>(
      `/bill-details/${billDetailId}/meters/settings`,
      { ...data, jointBillServiceAgreementId },
      {
        headers: {
          jointBillServiceId,
        },
      },
    )
    .then((r) => r.data);

export const selectDonationBill = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
  periodId: number,
  donationServiceProviderId: IDonationData["donationServiceProviderId"],
  amount: number,
): Promise<string> =>
  api
    .put<string>(
      `/joint-bill-service-donations/service-providers/${donationServiceProviderId}`,
      {
        periodId,
        amount,
      },
      {
        headers: {
          jointBillServiceId,
        },
      },
    )
    .then((r) => r.data);

export const unselectDonationBill = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
  periodId: number,
  donationServiceProviderId: IDonationData["donationServiceProviderId"],
): Promise<string> =>
  api
    .delete<string>(
      `/joint-bill-service-donations/service-providers/${donationServiceProviderId}`,
      {
        headers: {
          jointBillServiceId,
        },
        data: { periodId },
      },
    )
    .then((r) => r.data);

export const getBillDetailMeterRules = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
  billDetailId: IBillDetail["billDetailId"],
) =>
  api
    .get<IBillDetailMeterRulesResponse>(
      `/bill-details/${billDetailId}/meters/rules`,
      {
        headers: {
          jointBillServiceId,
        },
      },
    )
    .then((r) => r.data);

export const updateBillNumber = (
  billId: IOverviewJointBillService["jointBillServiceId"],
  billNumber: string,
): Promise<string> =>
  api
    .put<string>(`/bills/${billId}/number`, {
      billNumber,
    })
    .then((r) => r.data);

export const getBillCertificate = ({
  jointBillServiceId,
  jointBillServiceCertificateId,
}: {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  jointBillServiceCertificateId: IBillCertificate["jointBillServiceCertificateId"];
}) =>
  api
    .get<IFile>(`/certificates/${jointBillServiceCertificateId}`, {
      headers: {
        jointBillServiceId,
      },
    })
    .then((r) => r.data);
